"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Search, Plus, Map, Grid3X3, Filter, LayoutDashboard, MessageCircle, Bell } from "lucide-react"
import Image from "next/image"
import { AddSpotModal } from "@/components/add-spot-modal"
import { ThemeToggle } from "@/components/theme-toggle"
import { useAuth } from "@/lib/auth/context"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { User, LogOut, Settings, Shield, Menu } from "lucide-react"
import { useRouter, usePathname } from "next/navigation"
import { Badge } from "@/components/ui/badge"
import { useState, useEffect, useCallback } from "react"
import { createClient } from "@/lib/supabase/client"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { cn } from "@/lib/utils"
import Link from "next/link"

interface HeaderProps {
  viewMode?: "grid" | "map"
  setViewMode?: (mode: "grid" | "map") => void
  showMobileFilters?: boolean
  setShowMobileFilters?: (show: boolean) => void
}

// Header component - standardized across the app
export function Header({ viewMode, setViewMode, showMobileFilters, setShowMobileFilters }: HeaderProps) {
  const { isMobile } = usePlatform()
  const { user, signOut, loading } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false)
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false)
  const [showAddSpotModal, setShowAddSpotModal] = useState(false)
  const [userProfile, setUserProfile] = useState<any>(null)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)

  // Fetch user profile data (including role for admin check)
  const fetchUserProfile = useCallback(async (retryCount = 0) => {
    if (!user) return
    
    try {
      let accessToken: string | null = null
      try {
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
        const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
        const projectRef = match ? match[1] : 'default'
        const storageKey = `sb-${projectRef}-auth-token`
        const storedSession = localStorage.getItem(storageKey)
        if (storedSession) {
          const parsedSession = JSON.parse(storedSession)
          accessToken = parsedSession?.access_token || parsedSession?.currentSession?.access_token || null
        }
      } catch (e) {}
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`
      }
      
      const response = await fetch('/api/profile', {
        method: 'GET',
        credentials: 'include',
        headers,
      })
      
      const json = await response.json()
      
      if (response.ok && json?.success && json?.profile) {
        setUserProfile(json.profile)
      } else if (response.status === 401 && retryCount < 2) {
        setTimeout(() => fetchUserProfile(retryCount + 1), 1000)
      }
    } catch (error) {
      if (retryCount < 2) {
        setTimeout(() => fetchUserProfile(retryCount + 1), 1000)
      }
    }
  }, [user])

  // Fetch unread message count
  const fetchUnreadMessages = useCallback(async () => {
    if (!user?.id) return
    
    try {
      const supabase = createClient()
      const { count, error, data } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: false })
        .eq("recipient_id", user.id)
        .neq("is_read", true)
      
      if (error) return
      
      const unreadCount = count !== null ? count : (data?.length || 0)
      setUnreadMessageCount(unreadCount)
      setHasUnreadMessages(unreadCount > 0)
    } catch (error) {}
  }, [user])

  // Fetch unread notification count
  const fetchUnreadNotifications = useCallback(async () => {
    if (!user?.id) return
    
    try {
      const supabase = createClient()
      const { data: profile } = await supabase
        .from("users")
        .select("id")
        .eq("auth_id", user.id)
        .single()
      
      if (!profile?.id) return
      
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", profile.id)
        .eq("is_read", false)
        .neq("type", "chat_message")
      
      if (error) return
      
      setUnreadNotificationCount(count || 0)
      setHasUnreadNotifications((count || 0) > 0)
    } catch (error) {}
  }, [user])

  useEffect(() => {
    if (user && !loading) {
      const timer = setTimeout(() => {
        fetchUserProfile()
        fetchUnreadNotifications()
        fetchUnreadMessages()
      }, 1500)
      return () => clearTimeout(timer)
    } else {
      setUserProfile(null)
      setUnreadNotificationCount(0)
      setUnreadMessageCount(0)
    }
  }, [user, loading, fetchUserProfile, fetchUnreadNotifications, fetchUnreadMessages])
  
  // Real-time subscriptions
  useEffect(() => {
    if (!user?.id) return

    const supabase = createClient()
    const notificationsChannel = supabase
      .channel("header-notifications")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, () => fetchUnreadNotifications())
      .subscribe()

    const messagesChannel = supabase
      .channel("header-messages")
      .on("postgres_changes", { event: "*", schema: "public", table: "messages", filter: `recipient_id=eq.${user.id}` }, () => fetchUnreadMessages())
      .subscribe()

    window.addEventListener("messagesMarkedAsRead", fetchUnreadMessages)

    return () => {
      supabase.removeChannel(notificationsChannel)
      supabase.removeChannel(messagesChannel)
      window.removeEventListener("messagesMarkedAsRead", fetchUnreadMessages)
    }
  }, [user, fetchUnreadNotifications, fetchUnreadMessages])

  const handleSignOut = async () => {
    try {
      signOut()
      router.push("/")
    } catch (error) {
      router.push("/")
    }
  }

  const isAdmin = userProfile?.role === "admin" || user?.email === "contact@spotstosurf.com"

  return (
    <header className="fixed top-0 left-0 right-0 z-50 pt-safe">
      <div className="mx-auto max-w-7xl px-4 py-4 md:py-6">
        <div className={cn(
          "flex items-center justify-between px-6 py-3 rounded-[2.5rem] transition-all duration-300",
          "bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-white/20 dark:border-slate-800 shadow-2xl shadow-blue-900/5",
          "h-16 md:h-20"
        )}>
          {/* Logo & Brand */}
          <div className="flex items-center gap-4 lg:gap-8">
            <button
              onClick={() => {
                triggerHaptic('impact')
                router.push(user ? "/explore" : "/")
              }}
              className="flex items-center gap-3 group transition-all"
            >
              <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Image src="/spotstosurf-transparant.png" alt="Logo" width={40} height={40} className="object-contain" />
              </div>
              <div className="hidden sm:block text-left">
                <h1 className="text-xl lg:text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tighter italic leading-none">SPOTS2SURF</h1>
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 italic leading-none">Join the crew</p>
              </div>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1 bg-slate-100/50 dark:bg-slate-800/50 p-1 rounded-2xl border border-slate-200/20">
              {[
                { label: "Explore", href: "/explore", icon: Grid3X3 },
                { label: "Map", href: "/map", icon: Map },
                { label: "Messages", href: "/messages", icon: MessageCircle },
                ...(isAdmin ? [{ label: "Admin", href: "/admin", icon: Shield }] : []),
              ].map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => triggerHaptic('selection')}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                    pathname.startsWith(item.href)
                      ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-white shadow-sm scale-[1.02]"
                      : "text-slate-500 hover:text-slate-900 dark:hover:text-slate-200"
                  )}
                >
                  <item.icon className="w-3.5 h-3.5" />
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Search - Desktop only */}
            <div className="hidden md:flex items-center relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-colors group-focus-within:text-blue-500" />
              <Input
                placeholder="Search spots..."
                className="w-48 lg:w-64 h-12 pl-11 pr-4 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 font-bold text-xs uppercase tracking-widest focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
            </div>

            {user && (
              <div className="flex items-center gap-1 md:gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { triggerHaptic('impact'); router.push("/notifications"); }}
                  className="relative w-10 h-10 md:w-12 md:h-12 rounded-2xl text-slate-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                >
                  <Bell className="w-5 h-5" />
                  {unreadNotificationCount > 0 && (
                    <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-slate-900 shadow-sm" />
                  )}
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <ThemeToggle />
              
              {loading ? (
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
              ) : user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="w-10 h-10 md:w-12 md:h-12 rounded-2xl p-0 hover:scale-105 transition-transform"
                    >
                      <Avatar className="w-10 h-10 md:w-12 md:h-12 rounded-2xl border-2 border-blue-500/20">
                        <AvatarImage 
                          src={userProfile?.avatar_url || "/placeholder.svg"} 
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-blue-600 text-white font-black text-xs">
                          {userProfile?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-64 p-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl border-none rounded-[2rem] shadow-2xl"
                  >
                    <div className="px-4 py-4 mb-2 bg-slate-50 dark:bg-slate-800/50 rounded-2xl">
                      <p className="font-black uppercase tracking-tight text-slate-900 dark:text-white italic truncate">
                        {userProfile?.name || "Surfer"}
                      </p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 truncate italic">
                        {user.email}
                      </p>
                      {isAdmin && (
                        <Badge className="mt-3 bg-blue-600 hover:bg-blue-600 text-white text-[8px] font-black uppercase tracking-widest border-none px-2 py-0.5 rounded-lg">
                          Admin Access
                        </Badge>
                      )}
                    </div>
                    
                    <DropdownMenuItem onClick={() => router.push("/profile")} className="rounded-xl h-12 px-4 cursor-pointer">
                      <User className="mr-3 w-4 h-4 text-blue-500" />
                      <span className="font-bold text-sm">Profile</span>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem onClick={() => router.push("/settings")} className="rounded-xl h-12 px-4 cursor-pointer">
                      <Settings className="mr-3 w-4 h-4 text-slate-400" />
                      <span className="font-bold text-sm">Settings</span>
                    </DropdownMenuItem>

                    {isAdmin && (
                      <DropdownMenuItem onClick={() => router.push("/admin")} className="rounded-xl h-12 px-4 cursor-pointer bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 lg:hidden">
                        <Shield className="mr-3 w-4 h-4" />
                        <span className="font-bold text-sm">Admin Panel</span>
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator className="my-2 bg-slate-100 dark:bg-slate-800" />
                    
                    <DropdownMenuItem onClick={handleSignOut} className="rounded-xl h-12 px-4 cursor-pointer text-red-500 focus:text-red-500">
                      <LogOut className="mr-3 w-4 h-4" />
                      <span className="font-bold text-sm">Sign Out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-4">
                  <Link 
                    href="/auth/login" 
                    className="hidden sm:block text-xs font-black uppercase tracking-widest text-slate-500 hover:text-blue-600 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Button
                    onClick={() => router.push("/auth/register")}
                    className="h-12 md:h-14 px-6 md:px-8 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg shadow-blue-600/25 active:scale-[0.98] transition-all border-none"
                  >
                    Join the Crew
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <AddSpotModal open={showAddSpotModal} onOpenChange={setShowAddSpotModal} />
    </header>
  )
}
